import React from 'react';
import { motion } from 'framer-motion';
import { ASSIGNMENT_STATUS_CONFIG } from '@shared/constants/status';
import type { Assignment, Technician } from '@shared/types';
import { Calendar, MapPin, Edit2, PlayCircle, CheckCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AssignmentKanbanProps {
    assignments: Array<{ assignment: Assignment; technicians: Technician[] }>;
    onEditAssignment: (assignment: Assignment, techIds: string[]) => void;
    onStartWorksite: (assignmentId: string) => void;
    onFinishWorksite: (assignmentId: string) => void;
}

const KANBAN_COLUMNS = [
    { id: 'pending', label: 'En attente' },
    { id: 'in-progress', label: 'En cours' },
    { id: 'completed-by-tech', label: 'Terminé' },
    { id: 'closed', label: 'Clôturé' }
];

export const AssignmentKanban: React.FC<AssignmentKanbanProps> = ({
    assignments,
    onEditAssignment,
    onStartWorksite,
    onFinishWorksite
}) => {
    // Group assignments by status
    const groupedAssignments = React.useMemo(() => {
        const groups: Record<string, Array<{ assignment: Assignment; technicians: Technician[] }>> = {
            'pending': [],
            'in-progress': [],
            'completed-by-tech': [],
            'closed': []
        };

        assignments.forEach(item => {
            const status = item.assignment.status || 'pending';
            if (groups[status]) {
                groups[status].push(item);
            } else {
                // Fallback for unknown status
                if (!groups['pending']) groups['pending'] = [];
                groups['pending'].push(item);
            }
        });

        return groups;
    }, [assignments]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 h-full overflow-x-auto pb-4">
            {KANBAN_COLUMNS.map(column => {
                const columnAssignments = groupedAssignments[column.id] || [];
                const statusConfig = ASSIGNMENT_STATUS_CONFIG[column.id as keyof typeof ASSIGNMENT_STATUS_CONFIG] || ASSIGNMENT_STATUS_CONFIG['pending'];

                return (
                    <div key={column.id} className="min-w-[300px] flex flex-col h-full">
                        {/* Column Header */}
                        <div className={`p-3 rounded-t-xl border-b border-white/5 bg-white/5 flex items-center justify-between sticky top-0 z-10 backdrop-blur-md`}>
                            <div className="flex items-center gap-2">
                                <statusConfig.icon size={16} className={statusConfig.color.replace('bg-', 'text-').replace('/10', '')} />
                                <h3 className="font-semibold text-gray-200">{column.label}</h3>
                            </div>
                            <span className="px-2 py-0.5 rounded-full bg-white/10 text-xs text-gray-400">
                                {columnAssignments.length}
                            </span>
                        </div>

                        {/* Column Content */}
                        <div className="flex-1 bg-black/20 rounded-b-xl border border-white/5 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-250px)] scrollbar-hide">
                            {columnAssignments.map(({ assignment, technicians: assignedTechs }) => (
                                <motion.div
                                    key={assignment.id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="glass-panel p-3 hover:bg-white/10 transition-colors group relative"
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-medium text-gray-200 text-sm line-clamp-1" title={assignment.clientName}>
                                            {assignment.clientName}
                                        </h4>

                                        {/* Quick Actions (Hover) */}
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute right-2 top-2 bg-black/50 rounded-lg p-0.5 backdrop-blur-sm">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onEditAssignment(assignment, assignedTechs.map(t => t.id)); }}
                                                className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded"
                                            >
                                                <Edit2 size={12} />
                                            </button>

                                            {/* Status Actions */}
                                            {column.id === 'pending' && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onStartWorksite(assignment.id); }}
                                                    className="p-1 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded"
                                                    title="Démarrer"
                                                >
                                                    <PlayCircle size={12} />
                                                </button>
                                            )}
                                            {column.id === 'in-progress' && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onFinishWorksite(assignment.id); }}
                                                    className="p-1 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded"
                                                    title="Terminer"
                                                >
                                                    <CheckCircle size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div className="space-y-1.5 mb-3">
                                        <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                            <Calendar size={12} className="text-accent" />
                                            <span>{format(parseISO(assignment.startDate), 'dd/MM', { locale: fr })}</span>
                                            <span className="text-gray-600">→</span>
                                            <span>{format(parseISO(assignment.endDate), 'dd/MM', { locale: fr })}</span>
                                        </div>

                                        {(assignment.city || assignment.postalCode) && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500 line-clamp-1">
                                                <MapPin size={12} />
                                                <span>{assignment.postalCode} {assignment.city}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Techs */}
                                    <div className="flex -space-x-2 overflow-hidden py-1">
                                        {assignedTechs.map(tech => (
                                            <div
                                                key={tech.id}
                                                className="w-6 h-6 rounded-full flex items-center justify-center border-2 border-[#1a1b26] text-[10px] font-bold text-white shadow-sm"
                                                style={{ backgroundColor: tech.color }}
                                                title={tech.name}
                                            >
                                                {tech.name.charAt(0)}
                                            </div>
                                        ))}
                                        {assignedTechs.length === 0 && (
                                            <div className="w-6 h-6 rounded-full bg-gray-700/50 flex items-center justify-center border-2 border-[#1a1b26] text-[10px] text-gray-400">
                                                ?
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
